import React from "react";
import { AlertCircle, CheckCircle2, Clock, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { euro, fmtDate } from "../../lib/format";
import { toast } from "sonner";
import { withPrep } from "../../lib/issuer";

export default function ProposalStatusBanners({ cur, earlyActive, goToPayment, issuerName, locked, quote, renewRequested, renewing, setRenewRequested, setRenewing, token, totals }) {
  return (
    <>
      {quote.status === "accettato" && (
        <div data-testid="accepted-banner" className="mb-8 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 qf-pop">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
            <div>
              <p className="font-semibold text-emerald-800">Preventivo accettato e firmato</p>
              <p className="text-sm text-emerald-700">
                Firmato da {quote.accepted_name} il {fmtDate(quote.accepted_at)}. Le selezioni sono bloccate.
              </p>
            </div>
          </div>
          {quote.signature_image && (
            <div className="mt-4 grid sm:grid-cols-[auto,1fr] gap-4 items-center border-t border-emerald-200 pt-4">
              <div className="bg-card border border-emerald-200 rounded-lg p-2 w-fit">
                <img
                  data-testid="accepted-signature-image"
                  src={quote.signature_image}
                  alt="Firma del cliente"
                  className="h-16 max-w-[220px] object-contain"
                />
              </div>
              {quote.audit_trail && (
                <div data-testid="accepted-audit-trail" className="text-xs text-emerald-700 space-y-0.5">
                  <p className="inline-flex items-center gap-1.5 font-semibold text-emerald-800">
                    <ShieldCheck style={{ width: 14, height: 14 }} /> Traccia di controllo (Audit Trail)
                  </p>
                  <p>Firmatario: {quote.accepted_name}{quote.accepted_role ? ` — ${quote.accepted_role}` : ""}</p>
                  {quote.accepted_email && <p>Email: {quote.accepted_email}</p>}
                  <p>Data e ora: {fmtDate(quote.accepted_at)}</p>
                  {quote.audit_trail.ip_address && <p>Indirizzo IP: {quote.audit_trail.ip_address}</p>}
                  {quote.document_hash && (
                    <p className="font-mono text-[11px] text-emerald-600 break-all">
                      Sigillo SHA-256: {quote.document_hash.slice(0, 24)}…
                    </p>
                  )}
                  <a
                    data-testid="accepted-certificate-link"
                    href={`/c/${quote.public_token}`}
                    className="inline-flex items-center gap-1 font-semibold text-emerald-800 hover:underline mt-1"
                  >
                    <ShieldCheck style={{ width: 13, height: 13 }} /> Vedi Certificato di Firma
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {quote.status === "accettato" && quote.payment && (
        <div data-testid="payment-top-cta" className="mb-8 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-slate-200 bg-card px-5 py-3.5">
          <p className="text-sm text-slate-600">Pagamenti: acconto e saldo residuo.</p>
          <Button size="sm" variant="outline" data-testid="go-to-payment-top" onClick={() => goToPayment(null)}>Vedi riepilogo</Button>
        </div>
      )}
      {quote.status === "scaduto" && (
        <div data-testid="expired-banner" className="mb-8 bg-slate-100 border border-slate-300 rounded-xl px-5 py-4 qf-pop">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-slate-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-slate-800">
                Questa proposta commerciale è scaduta il {fmtDate(quote.due_date)}.
              </p>
              <p className="text-sm text-slate-600">I prezzi e le disponibilità non sono più garantiti.</p>
              {renewRequested ? (
                <p data-testid="renewal-confirmation" className="mt-3 text-sm font-medium text-emerald-700 inline-flex items-center gap-1.5">
                  <CheckCircle2 style={{ width: 16, height: 16 }} /> Richiesta inviata! {issuerName} ti ricontatterà a breve.
                </p>
              ) : (
                <Button
                  data-testid="request-renewal-button"
                  onClick={async () => {
                    setRenewing(true);
                    try {
                      await api.post(`/public/quotes/${token}/request-renewal`);
                      setRenewRequested(true);
                      toast.success(`Richiesta di riattivazione inviata ${withPrep("a", issuerName)}!`);
                    } catch (e) {
                      toast.error("Errore nell'invio della richiesta");
                    }
                    setRenewing(false);
                  }}
                  disabled={renewing}
                  className="mt-3 font-semibold"
                >
                  {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="break-words">Richiedi Riattivazione Offerta {withPrep("a", issuerName)}</span>}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {quote.status === "rifiutato" && (
        <div data-testid="rejected-banner" className="mb-8 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 qf-pop">
          <XCircle className="w-6 h-6 text-red-600 shrink-0" />
          <div>
            <p className="font-semibold text-red-800">Preventivo rifiutato</p>
            <p className="text-sm text-red-700">
              Rifiutato{quote.rejected_name ? ` da ${quote.rejected_name}` : ""} il {fmtDate(quote.rejected_at)}.
              {quote.rejected_reason ? ` Motivo: "${quote.rejected_reason}"` : ""}
            </p>
          </div>
        </div>
      )}

      {!locked && earlyActive && (
        <div data-testid="early-bird-banner" className="mb-6 flex items-center gap-3 bg-indigo-600 text-white rounded-xl px-5 py-4 qf-pop">
          <Clock className="w-6 h-6 shrink-0" />
          <div>
            <p className="font-semibold">Sconto a scadenza attivo</p>
            <p className="text-sm text-indigo-100">
              Accetta entro il {fmtDate(quote.early_discount_deadline)} e ottieni uno sconto extra di{" "}
              {quote.early_discount_type === "percent"
                ? `${quote.early_discount_value}%`
                : euro(quote.early_discount_value, cur)}
              {totals.scontoEarly > 0 ? ` (− ${euro(totals.scontoEarly, cur)})` : ""}.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
